import { Box } from '@mui/material';
import React from 'react';
import { themeColors } from '../JuvareTheme';
/**
 * Renders a card component that can be selected and optionally clicked.
 *
 * @param {SelectableCardProps} props - Component props.
 * @param {boolean} props.selected - Indicates whether the card is currently selected.
 * @param {() => void} [props.onCardClick] - Callback triggered on card click or keyboard interaction.
 * @param {string} [props.cardAriaLabel] - ARIA label for accessibility.
 * @param {SxProps<Theme> | SxProps<Theme>[]} [props.sx] - Additional styles to apply to the card.
 * @param {React.ReactNode} props.children - The content displayed inside the card.
 * @returns {JSX.Element} A selectable and optionally clickable card element.
 */
export const SelectableCard = ({ selected, onCardClick, cardAriaLabel, sx, children }) => {
    return (React.createElement(Box, { sx: [
            {
                position: 'relative',
                backgroundColor: 'background.paper',
                border: '1px solid ' + themeColors.border.main,
                borderRadius: 1,
                outline: selected ? '2px solid ' + themeColors.accent.blue.base : '1px solid transparent',
                cursor: onCardClick ? 'pointer' : undefined,
                '&:hover': onCardClick
                    ? {
                        backgroundColor: themeColors.accent.blue.subtlest,
                        outline: selected ? '2px solid ' + themeColors.accent.blue.base : '1px solid ' + themeColors.accent.blue.base,
                    }
                    : undefined,
                '&:focus-visible': onCardClick
                    ? {
                        outline: selected ? '4px solid ' + themeColors.accent.blue.base : '1px solid ' + themeColors.accent.blue.base,
                    }
                    : undefined,
                transition: 'background-color 150ms ease-in-out, outline-color 150ms ease-in-out',
            },
            ...(Array.isArray(sx) ? sx : [sx]),
        ], tabIndex: onCardClick ? 0 : undefined, onKeyDown: onCardClick
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    onCardClick();
                }
            }
            : undefined, onClick: onCardClick ? () => onCardClick() : undefined, role: 'button', "aria-label": cardAriaLabel, "aria-pressed": selected }, children));
};
